import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import {
  CreateHotLabEquipmentDto,
  HotLabEquipmentDto,
  HotLabEquipmentType,
  StorageLocationDto,
  SupplierDto,
  SupplierType,
} from '@services/dtos'
import { hotLabEquipmentSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Grid } from '@mui/material'
import {
  createHotLabEquipment,
  updateHotLabEquipment,
} from '@state/reducers/hotLabEquipmentReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import SelectField from '../inputs/SelectField'
import { HotLabEquipmentTypeLabels } from '@utils/constants'
import { getStorageLocations, setStorageLocationFilters } from '@state/reducers/storageLocationReducer'
import ServerAutocompleteMultiple from '../autocomplete/ServerAutocompleteMultiple'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import StorageLocationForm from './StorageLocationForm'
import { DateInputField } from '../inputs/DateInputField'
import moment from 'moment'
import { FormCrudHeader } from './FormCrudHeader'
import { CheckBoxInput } from '../inputs'
import SupplierForm from './SupplierForm'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { supplierService } from '@services/SupplierService'

const HOT_LAB_EQUIPMENT_TYPE_OPTIONS =
  (Object.entries(HotLabEquipmentTypeLabels) as Array<[HotLabEquipmentType, string]>)
    .map(([value, label]) => ({ label, value }))

type Props = {
  editingHotLabEquipment?: HotLabEquipmentDto | null
  onClose: () => void
  defaultValues?: Partial<CreateHotLabEquipmentDto>
}

const HotLabEquipmentForm: React.FC<Props> = ({
  editingHotLabEquipment,
  onClose,
  defaultValues
}) => {
  const {
    watch,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateHotLabEquipmentDto>({
    defaultValues: editingHotLabEquipment
      ? {
          type: editingHotLabEquipment.type,
          name: editingHotLabEquipment.name,
          hasPeriodicQualityChecks: editingHotLabEquipment.hasPeriodicQualityChecks,
          internalCheckIntervalDays: editingHotLabEquipment.internalCheckIntervalDays,
          externalCheckIntervalDays: editingHotLabEquipment.externalCheckIntervalDays,
          externalOrganizationId: editingHotLabEquipment.externalOrganization?.id,
          serial: editingHotLabEquipment.serial,
          manufacturer: editingHotLabEquipment.manufacturer,
          model: editingHotLabEquipment.model,
          installedAt: editingHotLabEquipment.installedAt
            ? new Date(editingHotLabEquipment.installedAt)
            : undefined,
          location: editingHotLabEquipment.location,
          notes: editingHotLabEquipment.notes,
          storageLocationIds: editingHotLabEquipment.storageLocations?.map(({ id }) => id) || [],
        }
      : { storageLocationIds: [], hasPeriodicQualityChecks: false, ...defaultValues },
    resolver: yupResolver(hotLabEquipmentSchema),
  })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
      dispatch(setStorageLocationFilters({ search: '' }))
    }
  }, [dispatch])

  const installedAt = watch('installedAt')

  const onSubmitForm = async (data: CreateHotLabEquipmentDto) => {
    setIsSubmitting(true)

    if (editingHotLabEquipment) {
      dispatch(updateHotLabEquipment({ id: editingHotLabEquipment.id, dto: data }))
        .unwrap()
        .then(() => {
          cb('Le matériel de laboratoire a été modifié avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le numéro de série n\'est pas déjà utilisé'))
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createHotLabEquipment(data))
        .unwrap()
        .then(() => {
          cb('Le matériel de laboratoire a été créé avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le numéro de série n\'est pas déjà utilisé'))
        .finally(() => setIsSubmitting(false))
    }
  }

  const fields1 = [
    { name: 'name', label: 'Nom', sm: '8' },
  ]
  const fields2 = [
    { name: 'serial', label: 'Numéro de série', sm: '6' },
    { name: 'manufacturer', label: 'Fabricant', sm: '6' },
    { name: 'model', label: 'Modèle', sm: '6' },
    { name: 'location', label: 'Emplacement', sm: '12' },
    { name: 'notes', label: 'Notes', sm: '12', type: 'textarea' }
  ]

  useEffect(() => {
    if (editingHotLabEquipment?.storageLocations?.length) {
      for (const storageLocation of editingHotLabEquipment.storageLocations) {
        itemCacheRef.current.set(storageLocation.id, storageLocation)
      }
    }
  }, [editingHotLabEquipment])

  const itemCacheRef = React.useRef(new Map<string, StorageLocationDto>())

  const fetchStorageLocations = async (search: string): Promise<StorageLocationDto[]> => {
    dispatch(setStorageLocationFilters({ search, page: 0 }))
    const page = await dispatch(getStorageLocations()).unwrap()
    const list = page?.datas ?? []
    for (const it of list) itemCacheRef.current.set(it.id, it)
    return list
  }

  const renderFields = (fields: any[]) => {
    return fields.map((field) => (
      <Grid item xs={12} sm={field.sm ?? 6} key={field.name}>
        <InputField
          control={control}
          name={field.name}
          label={field.label}
          type={field.type ?? 'text'}
          multiline={field.type === 'textarea'}
          rows={field.type === 'textarea' ? 2 : undefined}
        />
      </Grid>
    ))
  }

  const fetchExternalOrganizations = async (search: string) => {
    const page = await supplierService.getAll({
      search,
      type: SupplierType.EXTERNAL_ORGANIZATION,
    })
    return page?.datas ?? []
  }

  const hasPeriodicQualityChecks = watch('hasPeriodicQualityChecks')

  return (
    <div className="flex w-full h-full p-4">
      <form 
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }} 
        className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingHotLabEquipment}
        />

        <div className="max-w-screen-md mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Matériel de laboratoire
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              {renderFields(fields1)}

              <Grid item xs={12} sm={4}>
                <SelectField
                  control={control}
                  name={'type'}
                  options={HOT_LAB_EQUIPMENT_TYPE_OPTIONS}
                  value={watch('type')}
                  label={'Type'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DateInputField
                  label="Date d'installation"
                  name="installedAt"
                  value={
                    installedAt
                      ? moment(installedAt)
                      : null
                  }
                  onChange={(date) => {
                    setValue(
                      'installedAt',
                      date ? date.toDate() : undefined,
                    )
                  }}
                />
              </Grid>

              {renderFields(fields2)}

              <Grid item xs={12}>
                <CheckBoxInput
                  control={control}
                  name="hasPeriodicQualityChecks"
                  label="Contrôles qualité périodiques" />
              </Grid>

              {hasPeriodicQualityChecks && (<>
                <Grid item xs={12}>
                  <AddWrapperInput
                    legend="Organisme externe"
                    addTooltipTitle="Ajouter un fournisseur"
                    dialogContent={(close) => (
                      <SupplierForm
                        onClose={close}
                        defaultValues={{ type: SupplierType.EXTERNAL_ORGANIZATION }}
                      />
                    )}
                    input={
                      <ServerAutocomplete<SupplierDto>
                        fetchService={fetchExternalOrganizations}
                        onChange={(value: SupplierDto | null) => {
                          setValue('externalOrganizationId', value?.id ?? '')
                        }}
                        getValue={(value) => value?.id?.toString() || null}
                        getOptionLabel={(value) => {
                          const equipment = value as SupplierDto
                          return equipment ? equipment.name : ''
                        }}
                        debounceMs={300}
                        placeholder="Rechercher un organisme externe"
                        label="Organisme externe"
                        helperText={errors.externalOrganizationId?.message}
                        error={!!errors.externalOrganizationId}
                        minSearchLength={0}
                        initialOptions={
                          editingHotLabEquipment?.externalOrganization
                            ? [editingHotLabEquipment.externalOrganization]
                            : []
                        }
                        value={editingHotLabEquipment?.externalOrganization ?? null}
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InputField
                    control={control}
                    name="internalCheckIntervalDays"
                    label="Périodicité interne (jours)"
                    type="number"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InputField
                    control={control}
                    name="externalCheckIntervalDays"
                    label="Périodicité externe (jours)"
                    type="number"
                  />
                </Grid>
              </>)}

              <Grid item xs={12}>
                <AddWrapperInput
                  legend="Stockages"
                  addTooltipTitle="Ajouter un stockage"
                  dialogContent={(close) => (
                    <StorageLocationForm
                      onClose={() => {
                        close()
                      }}
                    />
                  )}
                  onAfterClose={() => {
                    dispatch(getStorageLocations())
                  }}
                  input={<Controller
                    name="storageLocationIds"
                    control={control}
                    render={({ field: { value: ids = [], onChange } }) => {
                      const selected = (ids as string[])
                        .map((id) =>
                          itemCacheRef.current.get(id)
                          ?? editingHotLabEquipment?.storageLocations?.find(s => s.id === id)
                        )
                        .filter(Boolean) as StorageLocationDto[]

                      return (
                        <ServerAutocompleteMultiple<StorageLocationDto>
                          values={selected}
                          onChange={(storageLocations) => {
                            onChange(storageLocations.map(({ id }) => id))
                          }}
                          fetchService={fetchStorageLocations}
                          getOptionLabel={({ name }) => name}
                          label="Stockages"
                          minSearchLength={1}
                          placeholder="Rechercher des stockages..."
                        />
                      )
                    }}
                  />}
              />
              </Grid>
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default HotLabEquipmentForm
